import {
  CircularProgress,
  IconButton,
  Input,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { useAuth0, User } from "@auth0/auth0-react";
import { useState, useEffect } from "react";
import LogoutIcon from "@mui/icons-material/Logout";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import {
  useWebApiV1ResetDemoDataMutation,
  useWebApiV1GetUserPreferencesQuery,
  useWebApiV1UpdateUserPreferencesMutation,
} from "@providers/hop-ord-server/api";
import { CheckCircle } from "@mui/icons-material";
import DemoProgressionControl from "./DemoProgressionControl";
import { useAppDispatch, useAppSelector } from "@hooks";
import { DEMO_TENANTS } from "../../constants";
import { AccountCircle } from "@mui/icons-material";
import { Select } from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import { Role } from "@enums";
import { setPreferredRole as setPreferredRoleAction } from "../../features/auth/auth0Slice";
import ExportDashboardCsvDialog from "./ExportDashboardCsvDialog";
import IosShareIcon from "@mui/icons-material/IosShare";
import { useCheckUserRole } from "@utils";
import {
  setProgressionActive,
  setIntervalSeconds,
  setManualDemoTrigger,
} from "../../features/settingsSlice";

interface Props {
  user: User;
}

const ResetStatusIcon = ({
  isLoading,
  isSuccess,
}: {
  isLoading: boolean;
  isSuccess: boolean;
}) => {
  if (isLoading)
    return <CircularProgress data-testid="reset-inprogress-icon" size={20} />;
  if (isSuccess)
    return <CheckCircle data-testid="data-success-icon" color="success" />;
  return <RestartAltIcon data-testid="reset-icon" />;
};

const UserMenu = ({ user }: Props) => {
  const { logout: auth0Logout } = useAuth0();
  const dispatch = useAppDispatch();

  // Timing stored in Redux
  const progressionActive = useAppSelector(
    (state) => state?.settings?.demoProgression?.progressionActive,
  );
  const intervalSeconds = useAppSelector(
    (state) => state?.settings?.demoProgression?.intervalSeconds,
  );
  const manualDemoTrigger = useAppSelector(
    (state) => state?.settings?.demoProgression?.manualDemoTrigger,
  );

  const tenants = useAppSelector((state) => state?.auth?.tenants);
  const roles = useAppSelector((state) => state?.auth?.roles) ?? [];

  const hasBothClinicalRoles =
    roles?.includes(Role.DOSIMETRIST) && roles?.includes(Role.RO);
  const isDemoTenant =
    tenants.filter((value) => DEMO_TENANTS.includes(value)).length > 0;
  const inNonDemoTenants =
    tenants.filter((value) => !DEMO_TENANTS.includes(value)).length > 0;
  const isRo = useCheckUserRole(Role.RO);

  // Only show the preferred role selector if the user has both RO and Dosimetrist roles and we are on the command center
  const onCommandCenter = location.pathname.endsWith("/commandcenter");

  const [
    resetDataMutation,
    { isLoading: resetLoading, isSuccess: resetSuccess },
  ] = useWebApiV1ResetDemoDataMutation();
  const { data: userPrefs } = useWebApiV1GetUserPreferencesQuery();
  const [updateUserPrefs, { isLoading: updatePrefsLoading }] =
    useWebApiV1UpdateUserPreferencesMutation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [preferredRole, setPreferredRole] = useState<string>("");
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const close = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (userPrefs?.preferred_role) {
      setPreferredRole(userPrefs.preferred_role);
      // keep Redux in sync with preference on initial load
      const role = userPrefs.preferred_role as Role;
      if (role === Role.DOSIMETRIST || role === Role.RO) {
        dispatch(setPreferredRoleAction(role));
      }
    }
  }, [userPrefs?.preferred_role]);

  const resetDemoData = async () => {
    dispatch(setProgressionActive(false));
    await resetDataMutation();
  };

  const toggleDemoProgression = () => {
    dispatch(setProgressionActive(!progressionActive));
  };

  const logout = () => {
    auth0Logout({ logoutParams: { returnTo: window.location.origin } });
    location.reload();
  };

  const onChangePreferredRole = async (event: SelectChangeEvent<string>) => {
    const value = event?.target?.value;
    if (!value) return;
    try {
      await updateUserPrefs({
        userPreferenceIn: { preferredRole: value },
      }).unwrap();

      setPreferredRole(value);
      const role = value as Role;
      if (role === Role.DOSIMETRIST || role === Role.RO) {
        dispatch(setPreferredRoleAction(role));
      }
    } catch {
      // no-op
    }
  };

  return (
    <>
      <DemoProgressionControl
        active={progressionActive}
        intervalSeconds={intervalSeconds}
        manualDemoTrigger={manualDemoTrigger}
        setManualDemoTrigger={(value: boolean) =>
          dispatch(setManualDemoTrigger(value))
        }
      />
      <IconButton onClick={handleClick} data-testid="user-menu-toggle-button">
        <AccountCircle sx={{ color: "white" }} />
      </IconButton>
      <Menu
        data-testid="user-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={close}
        slotProps={{
          list: { "aria-labelledby": "basic-button" },
        }}
      >
        <Typography
          data-testid="user-menu-username"
          variant="body2"
          align="center"
          sx={{ p: 2 }}
        >
          {user.name}
        </Typography>

        {hasBothClinicalRoles && (
          <Stack
            direction="row"
            paddingX={2}
            alignItems="center"
            justifyContent={"space-between"}
            sx={{ py: 1, gap: 2 }}
          >
            <Typography
              color={onCommandCenter ? "text.primary" : "text.secondary"}
            >
              Preferred role
            </Typography>
            <Select
              data-testid="preferred-role-select"
              value={preferredRole || ""}
              onChange={onChangePreferredRole}
              size="small"
              sx={{ minWidth: 180 }}
              disabled={updatePrefsLoading || !onCommandCenter}
            >
              <MenuItem value={Role.DOSIMETRIST}>Dosimetrist</MenuItem>
              <MenuItem value={Role.RO}>RO</MenuItem>
            </Select>
          </Stack>
        )}

        {isDemoTenant && (
          <>
            {inNonDemoTenants && (
              <Typography
                paddingX={2}
                variant="body2"
                maxWidth={220}
                color="error"
              >
                Controls may not work as you are in some non-demo tenants
              </Typography>
            )}
            <MenuItem
              onClick={resetDemoData}
              disabled={resetLoading}
              sx={{ gap: 1 }}
            >
              <ResetStatusIcon
                isLoading={resetLoading}
                isSuccess={resetSuccess}
              />
              Reset Demo Data
            </MenuItem>

            <MenuItem
              onClick={() => {
                dispatch(setManualDemoTrigger(true));
              }}
              disabled={resetLoading}
              sx={{ gap: 1 }}
            >
              <SkipNextIcon />
              Manually Progress Demo
            </MenuItem>
            <Stack
              direction="row"
              paddingX={2}
              alignItems="center"
              justifyContent={"space-between"}
            >
              <Typography>Auto Progress Demo</Typography>
              <Switch
                data-testid="demo-progression-switch"
                checked={progressionActive}
                onChange={toggleDemoProgression}
                slotProps={{
                  input: {
                    "aria-label": "controlled",
                  },
                }}
              />
            </Stack>
            <Stack
              direction="row"
              paddingX={2}
              alignItems="center"
              justifyContent={"space-between"}
            >
              <Typography>Every</Typography>
              <Input
                data-testid="manual-progression-button"
                sx={{ width: 50 }}
                type="number"
                value={intervalSeconds}
                onChange={(event) =>
                  dispatch(
                    setIntervalSeconds(Number(event.currentTarget.value)),
                  )
                }
              />
              <Typography>seconds</Typography>
            </Stack>
          </>
        )}

        {!isRo && roles.length > 0 && (
          <MenuItem onClick={() => setExportDialogOpen(true)} sx={{ gap: 1 }}>
            <IosShareIcon />
            Export Data
          </MenuItem>
        )}

        <MenuItem onClick={logout} sx={{ gap: 1 }}>
          <LogoutIcon /> Logout
        </MenuItem>
      </Menu>

      <ExportDashboardCsvDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
      />
    </>
  );
};

export default UserMenu;
